/**
 * Which items the canvas context menu is open on, where, and who opened it.
 *
 * One menu per view (`ItemContextMenuHost`), opened from any tile, card or
 * row (`useItemContextMenu`). The target ids outlive the menu on purpose:
 * Delete and Move open a dialog after the menu closes, and the dialog still
 * acts on them. Targets follow the selection store's contract: the host acts
 * on `flatItems ∩ targetIds`, never the ids alone.
 */

import { create } from 'zustand';
import type { WorkspaceItem } from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useSelectionStore, type ItemSurface } from '../../../stores/selectionStore';
import { safeAnalytics } from '../../../utils/analytics';
import { countBucket } from '../../../utils/analyticsCountBucket';
import { VIEW_CONTAINER, viewTabStop } from '../viewFocus';

/**
 * Where on screen the menu was opened from (analytics only): the item drawn
 * in the tree or a view, or the top bar's ⋮ on the open collection.
 */
export type ItemMenuUiSurface = 'tree' | 'tile' | 'card' | 'row' | 'topbar' | 'detail';

/** How the menu was opened (analytics only). */
export type ItemMenuTrigger = 'pointer' | 'keyboard' | 'longpress';

export interface ItemMenuPoint {
  readonly x: number;
  readonly y: number;
}

interface ItemMenuState {
  targetIds: readonly string[];
  /** Where the menu opens; null while it is closed. */
  anchor: ItemMenuPoint | null;
  /** The item that opened it, which gets focus back when it closes. */
  origin: HTMLElement | null;
  /**
   * The grid or tree around `origin`, captured at open: if `origin` has left
   * the page by the time the menu closes, focus goes to that view's tab stop.
   */
  container: HTMLElement | null;
  /** Counts opens, so each open is a fresh menu (and a fresh target count). */
  openId: number;
  /** The host that renders it. */
  surface: ItemSurface;
}

export const useItemMenuStore = create<ItemMenuState>()(() => ({
  targetIds: [],
  anchor: null,
  origin: null,
  container: null,
  openId: 0,
  surface: 'canvas',
}));

/** How the open was raised, for analytics only (never changes what opens). */
export interface ItemMenuOpenSource {
  readonly trigger: ItemMenuTrigger;
  readonly uiSurface: ItemMenuUiSurface;
}

/**
 * Opens the menu on `item`. On the canvas: inside the current selection it
 * targets the whole selection; outside it, the selection becomes just this
 * item (Finder's rule). In the tree it targets just the item and leaves the
 * canvas selection alone.
 */
export function openItemMenu(
  item: WorkspaceItem,
  anchor: ItemMenuPoint,
  origin: HTMLElement,
  surface: ItemSurface = 'canvas',
  source: ItemMenuOpenSource = {
    trigger: 'pointer',
    uiSurface: surface === 'tree' ? 'tree' : 'row',
  },
) {
  const { selectedIds, selectOnly } = useSelectionStore.getState();
  let targetIds: string[];
  if (surface === 'tree') {
    targetIds = [item.id];
  } else if (selectedIds.has(item.id)) {
    targetIds = [...selectedIds];
  } else {
    selectOnly(item.id);
    targetIds = [item.id];
  }
  useItemMenuStore.setState(({ openId }) => ({
    targetIds,
    anchor,
    origin,
    container: origin.closest<HTMLElement>(VIEW_CONTAINER),
    openId: openId + 1,
    surface,
  }));
  trackItemMenuOpened(source, targetIds.length);
}

/** ITEM_CONTEXT_MENU_OPENED, for this store's opens, the top bar's ⋮ and a detail page's ⋮. */
export function trackItemMenuOpened(source: ItemMenuOpenSource, targetCount: number) {
  safeAnalytics.track(ANALYTICS_EVENTS.ITEM_CONTEXT_MENU_OPENED, {
    surface: source.uiSurface,
    trigger: source.trigger,
    target_count_bucket: countBucket(targetCount),
  });
}

function focusTarget(origin: HTMLElement | null, container: HTMLElement | null) {
  if (origin?.isConnected) return origin;
  return viewTabStop(container);
}

/**
 * Closes the menu. `returnFocus` puts focus back on the item that opened it
 * or, when that item has left the page (trashed or moved by someone else),
 * on its view's roving tab stop, the item that took its place.
 */
export function closeItemMenu({ returnFocus }: { returnFocus: boolean }) {
  const { origin, container } = useItemMenuStore.getState();
  useItemMenuStore.setState({ anchor: null, origin: null, container: null });
  if (!returnFocus) return;
  focusTarget(origin, container)?.focus({ preventScroll: true });
}
